import React from 'react';
import Link from 'next/link';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { twJoin, twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { ProductInfoProps } from './types/CartProductInfo';
import { SubscriptionInfo } from '../SubscriptionInfo';

function CartProductInfo({
  url,
  name,
  discount,
  price,
  subscription,
  nukiId,
  variant,
  currency,
  quantity,
  toggleCartDisplay,
}: ProductInfoProps) {
  const { localizedLink } = useLocaleLink();
  const { formatCurrency, formatSubscriptionPrice } = useCurrencyFormatter();
  const t = useTranslations('CartProductInfo');

  const priceMode = variant?.priceMode ?? 'standard';

  let itemPriceWithoutDiscount = price || 0;
  let itemDiscount = discount || 0;
  // A discount above the price (a cart persisted before bundle discounts were clamped) is free.
  let itemPriceWithDiscount = Math.max(0, itemPriceWithoutDiscount - itemDiscount);

  switch (priceMode) {
    case 'final':
      // Prices are final and already include discounts
      itemPriceWithDiscount = price || 0;
      itemDiscount = discount || 0;
      itemPriceWithoutDiscount = itemPriceWithDiscount + itemDiscount;
      break;
    case 'adjusted':
      // Order confirmation: add discount back to show original
      itemPriceWithoutDiscount = (price || 0) + (discount || 0);
      itemPriceWithDiscount = price || 0;
      break;
    case 'standard':
    default:
      // Standard: price - discount
      break;
  }

  const showStrikethrough = variant?.showStrikethroughPrice !== false && !!discount;
  const showQuantityText = variant?.showQuantityText ?? false;

  const isLargeText = variant?.textSize === 'lg';
  const isMedText = variant?.textSize === 'md';
  const hasGap = isLargeText || isMedText;

  return (
    <div>
      <div
        className={twJoin(
          'flex justify-between font-bold text-neutral-100',
          hasGap && 'gap-2 sm:gap-3',
        )}
      >
        <p
          className={twMerge(
            'line-clamp-2 overflow-hidden text-ellipsis',
            isLargeText && 'sm:text-lg md:text-2xl',
            isMedText && 'sm:text-xl md:text-xl',
          )}
        >
          {url ? (
            <Link href={localizedLink(url)} onClick={toggleCartDisplay}>
              {name}
            </Link>
          ) : (
            name
          )}
        </p>

        <div className="ml-2 whitespace-nowrap">
          {showStrikethrough ? (
            <p
              className={twMerge(
                'text-sm font-light text-neutral-100 line-through',
                (isLargeText || isMedText) && 'sm:text-base md:text-lg',
              )}
            >
              {formatCurrency(itemPriceWithoutDiscount, { currency })}
            </p>
          ) : (
            ''
          )}
          <p
            className={twMerge(
              isLargeText && 'sm:text-lg md:text-xl',
              isMedText && 'sm:text-md md:text-lg',
            )}
          >
            {subscription?.period.recurring
              ? formatSubscriptionPrice(subscription.period.recurring, { currency })
              : formatCurrency(itemPriceWithDiscount, {
                  currency,
                })}
          </p>
        </div>
      </div>

      <div className="mt-2 space-y-2">
        {showQuantityText && quantity && (
          <div className="mb-2 font-light text-neutral-50 sm:text-md">
            <span>
              {t('Quantity')} {quantity}
            </span>
          </div>
        )}

        {nukiId && (
          <div
            className={twMerge(
              'font-light text-neutral-50',
              isLargeText && 'sm:text-lg md:text-xl',
              isMedText && 'sm:text-md',
            )}
          >
            {t('Connected to Nuki ID')} <span className="font-medium">{nukiId}</span>
          </div>
        )}
      </div>

      {subscription && (
        <SubscriptionInfo
          subscription={subscription}
          isLargeText={isLargeText}
          isMedText={isMedText}
        />
      )}
    </div>
  );
}

export default CartProductInfo;
